/* import __COLOCATED_TEMPLATE__ from './used-by-cell.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

const ICON_MAP: Record<string, InterfaceIconName> = {
  Workflow: 'workflows',
  Procedure: 'fin',
  'Fin Task': 'custom-attribute',
  'Resolution Bot': 'custom-attribute',
  Behavior: 'bot-filled',
  'Inbox Workflow': 'workflows',
  Macro: 'saved-reply',
};

const TYPE_DISPLAY_KEY_MAP: Record<string, string> = {
  'Fin Task': 'workflow-connector.list.used-in-tooltip.type-display-names.fin-task',
};

interface DependentObject {
  title: string;
  type: string;
  route: string;
}

interface DependentObjectWithIcon extends DependentObject {
  icon: InterfaceIconName | undefined;
}

interface GroupedDependentObjects {
  type: string;
  icon: InterfaceIconName | undefined;
  items: DependentObject[];
}

interface Signature {
  Args: {
    action: {
      dependentObjects: DependentObject[];
    };
  };
}

export default class UsedByCell extends Component<Signature> {
  @service declare intl: IntlService;

  get dependentObjects(): DependentObject[] {
    return this.args.action.dependentObjects ?? [];
  }

  get groupedDependentObjects(): GroupedDependentObjects[] {
    let groups = new Map<string, DependentObject[]>();

    for (let obj of this.dependentObjects) {
      if (!groups.has(obj.type)) {
        groups.set(obj.type, []);
      }
      groups.get(obj.type)!.push(obj);
    }

    return Array.from(groups.entries()).map(([type, items]) => {
      let displayKey = TYPE_DISPLAY_KEY_MAP[type];
      return {
        type: displayKey ? this.intl.t(displayKey) : type,
        icon: ICON_MAP[type],
        items,
      };
    });
  }

  get firstItem(): DependentObjectWithIcon | undefined {
    let item = this.dependentObjects[0];
    if (!item) {
      return undefined;
    }
    return {
      ...item,
      icon: ICON_MAP[item.type],
    };
  }

  get additionalCount(): number {
    return this.dependentObjects.length - 1;
  }

  get hasMoreItems(): boolean {
    return this.additionalCount > 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::UsedByCell': typeof UsedByCell;
    'workflow-connector/used-by-cell': typeof UsedByCell;
  }
}
